import { GetIssueResponseDto } from '@issuemanager/api-client';
import { useUpdateSLAStatus } from '@issuemanager/client/features/issues';
import { IssueStatusEnum, SLAStateEnum } from '@issuemanager/constants';
import { FormControlLabel, Switch, styled } from '@mui/material';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SLAConfirmModal } from './disable-sla-confirm-modal';
import { useCounter } from './hooks';

interface Props {
  issue: GetIssueResponseDto;
}

const StyledFormControlLabel = styled(FormControlLabel)`
  margin-right: 0;
`;

export function SLASwitch({ issue }: Props) {
  const { t } = useTranslation('issue');
  const {
    daysLeft,
    hoursLeft,
    minutesLeft,
    killInterval,
    refreshTimeLeft,
    initiateInterval,
  } = useCounter({
    createdAt: issue.createdAt,
    slaDays: issue.slaDays,
  });

  const [isSLAModalOpen, setIsSLAModalOpen] = useState(false);

  const updateSLAStatus = useUpdateSLAStatus({
    code: issue.code,
  });

  const isFinalStatus =
    issue.status === IssueStatusEnum.CLOSED ||
    issue.status === IssueStatusEnum.DISCARDED;

  const handleClose = (): void => {
    setIsSLAModalOpen(false);
  };

  const handleAccept = async (data: { message: string }): Promise<void> => {
    if (!issue.slaEnabled) {
      await updateSLAStatus.mutateAsync({
        state: SLAStateEnum.ACTIVATED,
        reason: data.message,
      });
    } else {
      await updateSLAStatus.mutateAsync({
        state: SLAStateEnum.DEACTIVATED,
        reason: data.message,
      });
    }
    killInterval();
    handleClose();
  };

  const handleChange = async (_event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSLAModalOpen(true);
    refreshTimeLeft();
    initiateInterval();
  };

  const canEditSlaEnabled =
    issue.editableFields?.includes('slaEnabled') ?? false;
  const getSLATimeValues = () => ({
    days: issue.slaDays,
    daysLeft,
    hoursLeft: String(hoursLeft).padStart(2, '0'),
    minutesLeft: String(minutesLeft).padStart(2, '0'),
  });

  return (
    <>
      <StyledFormControlLabel
        control={
          <Switch
            checked={issue.slaEnabled}
            onChange={handleChange}
            disabled={isFinalStatus || !canEditSlaEnabled}
            size='small'
            data-testid='sla-switch'
          />
        }
        label={
          issue.slaEnabled
            ? t('view.sla.banner.activeSLA')
            : t('view.sla.banner.deactiveSLA')
        }
      />
      {issue.slaEnabled &&
        (issue.slaDueDays && issue.slaDueDays < 0 ? (
          <Trans
            t={t}
            i18nKey='view.sla.banner.overdueSLADays'
            values={{
              days: -issue.slaDueDays,
            }}
          />
        ) : (
          <Trans
            t={t}
            i18nKey='view.sla.banner.activeSLAMessage'
            values={getSLATimeValues()}
          />
        ))}
      <SLAConfirmModal
        isOpen={isSLAModalOpen}
        onAccept={handleAccept}
        onClose={handleClose}
        isLoading={updateSLAStatus.isPending}
        state={
          issue.slaEnabled ? SLAStateEnum.DEACTIVATED : SLAStateEnum.ACTIVATED
        }
      />
    </>
  );
}
